import React, { useEffect, useState } from "react";
import calculateLcoe from "./utils/finance";

const FALLBACK_RATES = { ZAR: 18, EUR: 0.9, ZMW: 24 };

export default function FinancialPage({
  systemType,
  setSystemType,
  costs,
  setCosts,
  kWp,
  annualMWh,
  siteAreaHa,
  currency,
  setCurrency,
  financials,
  setFinancials,
}) {
  const [mode, setMode] = useState("perKwp");
  const [fxRates, setFxRates] = useState(FALLBACK_RATES);
  const sumPerKwp = (items) =>
    items.reduce((s, i) => s + (i.children ? sumPerKwp(i.children) : i.perKwp), 0);

  const flatten = (items, depth = 0, path = []) =>
    items.flatMap((it) => {
      const currentPath = [...path, it.id];
      const perKwp = it.children ? sumPerKwp(it.children) : it.perKwp;
      const row = { ...it, perKwp, depth, path: currentPath, leaf: !it.children };
      return [row, ...(it.children ? flatten(it.children, depth + 1, currentPath) : [])];
    });

  const rows = flatten(costs[systemType]);

  useEffect(() => {
    if (typeof fetch === "undefined") return;
    fetch("https://open.er-api.com/v6/latest/USD")
      .then((res) => res.json())
      .then((data) => {
        if (data && data.rates) {
          setFxRates({
            ZAR: data.rates.ZAR,
            EUR: data.rates.EUR,
            ZMW: data.rates.ZMW,
          });
        }
      })
      .catch(() => {});
  }, []);

  const updateItem = (path, perKwp) => {
    setCosts((prev) => {
      const next = { ...prev };
      const update = (items, depth = 0) =>
        items.map((it) => {
          if (it.id === path[depth]) {
            if (depth === path.length - 1) {
              return { ...it, perKwp };
            }
            if (it.children) {
              return { ...it, children: update(it.children, depth + 1) };
            }
          }
          return it;
        });
      next[systemType] = update(prev[systemType]);
      return next;
    });
  };

  const toggleInclude = (path, includeInQuote) => {
    setCosts((prev) => {
      const next = { ...prev };
      const update = (items, depth = 0) =>
        items.map((it) => {
          if (it.id === path[depth]) {
            if (depth === path.length - 1) {
              return { ...it, includeInQuote };
            }
            if (it.children) {
              return { ...it, children: update(it.children, depth + 1) };
            }
          }
          return it;
        });
      next[systemType] = update(prev[systemType]);
      return next;
    });
  };

  const formatMoney = (n) =>
    n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatWhole = (n) => Math.round(n).toLocaleString();

  const totalPerKwp = sumPerKwp(costs[systemType]);
  const totalCapex = totalPerKwp * kWp;
  const rate = fxRates[currency];
  const finance = financials[systemType];

  const updateFinance = (key, value) => {
    setFinancials((prev) => ({
      ...prev,
      [systemType]: { ...prev[systemType], [key]: value },
    }));
  };

  const lcoeResult = calculateLcoe(
    { capex: totalCapex, annualMWh, siteAreaHa, kWp },
    finance
  );

  return (
    <div className="financial-page">
      <h2>Financial Analysis</h2>
      <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
        <label>
          Variant
          <select value={systemType} onChange={(e) => setSystemType(e.target.value)}>
            <option value="peg">PEG</option>
            <option value="fixed">Fixed tilt</option>
            <option value="tracker">Tracker</option>
          </select>
        </label>
        <label>
          Edit
          <select value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="perKwp">$/kWp</option>
            <option value="total">Total $</option>
          </select>
        </label>
        <label>
          Currency
          <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
            <option value="ZAR">ZAR</option>
            <option value="EUR">EUR</option>
            <option value="ZMW">ZMW</option>
          </select>
        </label>
      </div>
      <div style={{ display: "flex", gap: 24 }}>
        <div style={{ flex: 1 }}>
          <table className="cost-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>$/kWp</th>
                <th>Total $</th>
                <th>Total {currency}</th>
                <th>Include in Quote</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((item) => {
                const total = item.perKwp * kWp;
                const converted = rate ? total * rate : null;
                return (
                  <tr key={item.path.join("/")}>
                    <td style={{ paddingLeft: item.depth * 16, fontWeight: item.leaf ? "normal" : "bold" }}>
                      {item.label}
                    </td>
                    <td>
                      {mode === "perKwp" && item.leaf ? (
                        <input
                          type="number"
                          value={item.perKwp}
                          onChange={(e) => updateItem(item.path, +e.target.value || 0)}
                        />
                      ) : (
                        formatMoney(item.perKwp)
                      )}
                    </td>
                    <td>
                      {mode === "total" && item.leaf ? (
                        <input
                          type="number"
                          value={formatWhole(total)}
                          onChange={(e) =>
                            updateItem(item.path, kWp ? (+e.target.value || 0) / kWp : 0)
                          }
                        />
                      ) : (
                        formatWhole(total)
                      )}
                    </td>
                    <td>{converted !== null ? formatWhole(converted) : "..."}</td>
                    <td>
                      {item.leaf ? (
                        <input
                          type="checkbox"
                          checked={item.includeInQuote || false}
                          onChange={(e) => toggleInclude(item.path, e.target.checked)}
                        />
                      ) : null}
                    </td>
                  </tr>
                );
              })}
              <tr>
                <td><b>Total</b></td>
                <td><b>{formatMoney(totalPerKwp)}</b></td>
                <td><b>{formatWhole(totalCapex)}</b></td>
                <td><b>{rate ? formatWhole(totalCapex * rate) : "..."}</b></td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div style={{ flex: 1 }}>
          <h3>Other project costs</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <label>
              Land cost ($/ha/yr)
              <input
                type="number"
                value={finance.landCostPerHa}
                onChange={(e) => updateFinance("landCostPerHa", +e.target.value || 0)}
              />
            </label>
            <label>
              O&amp;M ($/kWp/yr)
              <input
                type="number"
                value={finance.oAndMPerKwp}
                onChange={(e) => updateFinance("oAndMPerKwp", +e.target.value || 0)}
              />
            </label>
            <label>
              Financing rate (%)
              <input
                type="number"
                value={(finance.financingRate * 100).toFixed(2)}
                onChange={(e) =>
                  updateFinance("financingRate", (+e.target.value || 0) / 100)
                }
              />
            </label>
            <label>
              Annual degradation (%)
              <input
                type="number"
                value={finance.annualDegradation}
                onChange={(e) => updateFinance("annualDegradation", +e.target.value || 0)}
              />
            </label>
            <label>
              Lifetime (years)
              <input
                type="number"
                value={finance.lifetimeYears}
                onChange={(e) => updateFinance("lifetimeYears", +e.target.value || 0)}
              />
            </label>
          </div>
          <h3>LCOE</h3>
          <div className="item"><span>LCOE</span><b>${lcoeResult.lcoe.toFixed(3)}</b></div>
          <div className="item">
            <span>Lifetime cost (NPV)</span>
            <b>${formatWhole(lcoeResult.npvCost)}</b>
          </div>
          <div className="item">
            <span>Lifetime energy (kWh)</span>
            <b>{formatWhole(lcoeResult.npvEnergy)}</b>
          </div>
        </div>
      </div>
    </div>
  );
}
